import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { Observable, isObservable, map } from 'rxjs';

/**
 * Implement this on any route component that holds user input worth
 * protecting — the booking form being the obvious one.
 *
 * `canDeactivate()` answers "is it safe to leave?". Return true when there is
 * nothing to lose (pristine form, or already submitted); return false when
 * there are unsaved changes and the guard should prompt.
 */
export interface CanComponentDeactivate {
  canDeactivate(): boolean | Promise<boolean> | Observable<boolean>;
  /** Optional per-component wording for the confirmation prompt. */
  unsavedChangesMessage?: string;
}

export const DEFAULT_UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave this page?';

/**
 * Blocks navigation away from a component with unsaved changes until the user
 * confirms. Attach it to a route with:
 *
 *   { path: 'book', component: BookingComponent, canDeactivate: [unsavedChangesGuard] }
 *
 * It uses the native confirm() dialog to stay dependency-free. To swap in a
 * PrimeNG ConfirmDialog later, replace `confirmLeave` with a call returning a
 * Promise<boolean> — the guard already handles async results.
 */
export const unsavedChangesGuard: CanDeactivateFn<CanComponentDeactivate> = (component) => {
  const view = inject(DOCUMENT).defaultView;

  const confirmLeave = (): boolean =>
    view?.confirm(component.unsavedChangesMessage ?? DEFAULT_UNSAVED_CHANGES_MESSAGE) ?? true;

  // A component that never opted in is always safe to leave.
  if (typeof component?.canDeactivate !== 'function') {
    return true;
  }

  const safeToLeave = component.canDeactivate();

  if (typeof safeToLeave === 'boolean') {
    return safeToLeave || confirmLeave();
  }

  if (isObservable(safeToLeave)) {
    return safeToLeave.pipe(map((safe) => safe || confirmLeave()));
  }

  return Promise.resolve(safeToLeave).then((safe) => safe || confirmLeave());
};
